import { byId, clear, el, setText } from "../dom";
import type { FleetResponse, Route, Vehicle } from "../types";
import { emptyState, errorState } from "./states";

export interface FleetHandlers {
  onReload(): void;
  onCreateVehicle(plate: string, label: string): void;
  onSaveVehicle(vehicleId: string, plate: string, label: string): void;
  onCreateRoute(name: string): void;
  onSaveRoute(routeId: string, name: string): void;
}

export interface FleetController {
  setFleet(fleet: FleetResponse | null): void;
  setBusy(busy: boolean): void;
}

export function initFleet(handlers: FleetHandlers): FleetController {
  const vehicleList = byId<HTMLUListElement>("fleet-vehicles");
  const routeList = byId<HTMLUListElement>("fleet-routes");
  const vehiclePlate = byId<HTMLInputElement>("fleet-vehicle-plate");
  const vehicleLabel = byId<HTMLInputElement>("fleet-vehicle-label");
  const createVehicleButton = byId<HTMLButtonElement>("fleet-vehicle-create");
  const routeName = byId<HTMLInputElement>("fleet-route-name");
  const createRouteButton = byId<HTMLButtonElement>("fleet-route-create");
  const reloadButton = byId<HTMLButtonElement>("fleet-reload");
  const summary = byId<HTMLParagraphElement>("fleet-manage-summary");

  const errorSlot = el("div", { className: "operation__error" });
  reloadButton.closest(".card__actions")?.insertAdjacentElement("afterend", errorSlot);

  let fleet: FleetResponse | null = null;
  let loaded = false;

  function vehicleRow(vehicle: Vehicle): HTMLElement {
    const item = el("li", { className: "fleet__item" });
    const plate = el("input", { className: "fleet__input fleet__input--plate" });
    plate.value = vehicle.plate;
    plate.maxLength = 10;
    plate.setAttribute("aria-label", "Placa del vehículo");
    const label = el("input", { className: "fleet__input" });
    label.value = vehicle.label;
    label.maxLength = 60;
    label.setAttribute("aria-label", "Etiqueta del vehículo");
    const save = el("button", { className: "btn btn--ghost btn--sm", text: "Guardar" });
    save.type = "button";
    save.addEventListener("click", () => handlers.onSaveVehicle(vehicle.id, plate.value, label.value));
    item.append(plate, label, save);
    return item;
  }

  function routeRow(route: Route): HTMLElement {
    const item = el("li", { className: "fleet__item" });
    const name = el("input", { className: "fleet__input" });
    name.value = route.name;
    name.maxLength = 60;
    name.setAttribute("aria-label", "Nombre de la ruta");
    const save = el("button", { className: "btn btn--ghost btn--sm", text: "Guardar" });
    save.type = "button";
    save.addEventListener("click", () => handlers.onSaveRoute(route.id, name.value));
    item.append(name, save);
    return item;
  }

  function render(): void {
    clear(vehicleList);
    clear(routeList);
    clear(errorSlot);

    if (fleet === null) {
      if (loaded) {
        errorSlot.append(
          errorState({
            title: "No pudimos cargar la flota",
            message: "Revisa la conexión e intenta de nuevo.",
            onRetry: handlers.onReload,
          }),
        );
      }
      setText(summary, "Sin datos");
      return;
    }

    if (fleet.vehicles.length === 0) {
      vehicleList.append(
        emptyState({
          iconName: "i-truck",
          title: "Sin vehículos",
          message: "Agrega el primer vehículo para generar códigos de conductor.",
        }),
      );
    } else {
      for (const vehicle of fleet.vehicles) vehicleList.append(vehicleRow(vehicle));
    }

    if (fleet.routes.length === 0) {
      routeList.append(
        emptyState({
          iconName: "i-pin",
          title: "Sin rutas",
          message: "Agrega al menos una ruta para asociarla a los turnos.",
        }),
      );
    } else {
      for (const route of fleet.routes) routeList.append(routeRow(route));
    }

    setText(
      summary,
      `${fleet.operator.name} · ${fleet.vehicles.length} ${fleet.vehicles.length === 1 ? "vehículo" : "vehículos"} · ${fleet.routes.length} ${fleet.routes.length === 1 ? "ruta" : "rutas"}`,
    );
  }

  reloadButton.addEventListener("click", handlers.onReload);
  createVehicleButton.addEventListener("click", () => {
    const plate = vehiclePlate.value.trim();
    const label = vehicleLabel.value.trim();
    if (plate === "" || label === "") return;
    handlers.onCreateVehicle(plate, label);
  });
  createRouteButton.addEventListener("click", () => {
    const name = routeName.value.trim();
    if (name === "") return;
    handlers.onCreateRoute(name);
  });

  return {
    setFleet(next) {
      fleet = next;
      if (next !== null) {
        loaded = true;
        vehiclePlate.value = "";
        vehicleLabel.value = "";
        routeName.value = "";
      }
      render();
    },

    setBusy(busy) {
      createVehicleButton.disabled = busy;
      createRouteButton.disabled = busy;
      setText(createVehicleButton, busy ? "Guardando…" : "Agregar vehículo");
      setText(createRouteButton, busy ? "Guardando…" : "Agregar ruta");
    },
  };
}
